/**
 * SelectInstancesPage
 *
 * The first step after login: pick the source → target instance pair before any workflow type is
 * chosen. Once both are picked, the pair's capabilities are checked against BOTH instances (the
 * operator may hold different DWI roles per instance) and the per-instance account status is
 * verified (archived / missing account each get their own message). Continue only unlocks when at
 * least one migration type is allowed for the pair; the workflow-cards page then gates each card
 * on the same pair capabilities.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, ShieldAlert, XCircle } from "lucide-react";

import { AccessSummary } from "../components/AccessSummary";
import { AppSidebar } from "../components/app-sidebar";
import { InstancePicker } from "../components/InstancePicker";
import { SiteHeader } from "../components/site-header";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import { usePairCapabilities, type InstanceCapability } from "../lib/api";
import { useAuthStore } from "../store/useAuthStore";
import { useInstancePairStore } from "../store/useInstancePairStore";

/** The user-facing wording per account status — keep in sync with UserInstanceAuthorizer. */
function statusMessage(inst: InstanceCapability): string | null {
  switch (inst.userStatus) {
    case "ARCHIVED":
      return `Your account is archived on ${inst.name}. Contact your administrator to restore it.`;
    case "NOT_FOUND":
      return `No account with your username exists on ${inst.name}. Contact your administrator.`;
    case "ERROR":
      return `Could not verify your account on ${inst.name}. Please try again or contact your administrator.`;
    default:
      return null;
  }
}

const SelectInstancesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isDefaultUser } = useAuthStore();

  const sourceServiceId = useInstancePairStore((s) => s.sourceServiceId);
  const targetServiceId = useInstancePairStore((s) => s.targetServiceId);
  const bothPicked = sourceServiceId != null && targetServiceId != null;

  const { data: caps, isFetching } = usePairCapabilities(sourceServiceId, targetServiceId);
  const canMigrateProcess = !!caps?.canMigrateProcess;
  const canMigrateOntology = !!caps?.canMigrateOntology;
  const canContinue = bothPicked && !isFetching && (canMigrateProcess || canMigrateOntology);

  // The DEFAULT admin manages the MU itself and never migrates — no pair to select.
  React.useEffect(() => {
    if (isDefaultUser) navigate("/workflows", { replace: true });
  }, [isDefaultUser, navigate]);

  const accountIssues = (caps?.instances ?? [])
    .map(statusMessage)
    .filter((m): m is string => m != null);
  const bothAccountsActive =
    (caps?.instances?.length ?? 0) > 0 &&
    (caps?.instances ?? []).every((i) => i.userStatus === "ACTIVE");
  const showNoPermission =
    bothPicked && !isFetching && !!caps && bothAccountsActive && !canMigrateProcess && !canMigrateOntology;
  const showCheckFailed = bothPicked && !isFetching && !!caps?.checkFailed;

  return (
    <SidebarProvider
      style={{
        "--sidebar-width": "16rem",
        "--header-height": "3rem",
      } as React.CSSProperties}
      className="min-h-screen"
    >
      <AppSidebar variant="inset" />
      <SidebarInset className="pt-0">
        <SiteHeader />
        <main className="mx-auto w-full max-w-4xl px-6 py-10">
          <header className="mb-8">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Select Instances</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Choose the source and target instance for this migration session. Your role and
              permissions are checked on both instances before any workflow becomes available.
            </p>
          </header>

          <AccessSummary />

          <InstancePicker />

          {/* ── Pair verification results ── */}
          {bothPicked && (
            <section className="mt-6 space-y-3">
              {isFetching ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking your account and permissions on both instances…
                </div>
              ) : (
                <>
                  {/* Account problems (archived / missing / unverifiable) — one banner per instance */}
                  {accountIssues.map((message) => (
                    <div
                      key={message}
                      className="flex items-start gap-2.5 rounded-lg border border-destructive/40 bg-destructive/10 p-3.5 text-sm text-destructive"
                    >
                      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{message}</span>
                    </div>
                  ))}

                  {showCheckFailed && (
                    <div className="flex items-start gap-2.5 rounded-lg border border-destructive/40 bg-destructive/10 p-3.5 text-sm text-destructive">
                      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>Could not check your permissions. Please try again or contact your administrator.</span>
                    </div>
                  )}

                  {showNoPermission && (
                    <div className="flex items-start gap-2.5 rounded-lg border border-destructive/40 bg-destructive/10 p-3.5 text-sm text-destructive">
                      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        You do not have permission to perform any migration between these instances.
                        Contact your administrator to be granted a migration role.
                      </span>
                    </div>
                  )}

                  {/* Per-workflow availability for the pair */}
                  {caps && !caps.checkFailed && (
                    <div className="rounded-lg border border-border bg-card/60 p-4">
                      <p className="mb-3 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/70">
                        Available migrations for this pair
                      </p>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          {canMigrateProcess ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground/60" />
                          )}
                          <span className={canMigrateProcess ? "text-foreground" : "text-muted-foreground"}>
                            Process migration (incl. linked processes)
                          </span>
                        </li>
                        <li className="flex items-center gap-2">
                          {canMigrateOntology ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground/60" />
                          )}
                          <span className={canMigrateOntology ? "text-foreground" : "text-muted-foreground"}>
                            Ontology migration (incl. action effects)
                          </span>
                        </li>
                      </ul>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {/* ── Continue ── */}
          <div className="mt-8 flex justify-end">
            <button
              onClick={() => navigate("/workflows")}
              disabled={!canContinue}
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-foreground px-4 py-2.5 text-sm font-medium text-background transition hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default SelectInstancesPage;
